import axios from 'axios';

// Use environment variable or fallback to production URL
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost/financials/backend'
  : 'https://finances.abdulhananjavaid.com/backend'; // Your domain

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for session management
});

export interface Expense {
  id?: number;
  category_id: number;
  amount: number;
  description: string;
  is_recurring: boolean;
  category_name?: string;
  category_color?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Income {
  id?: number;
  source_name: string;
  amount: number;
  frequency: 'monthly' | 'weekly' | 'daily' | 'yearly';
  created_at?: string;
  updated_at?: string;
}

export interface Account {
  id?: number;
  account_name: string;
  account_type: 'savings' | 'checking' | 'investment' | 'cash' | 'other';
  current_balance: number;
  description: string;
  created_at?: string;
  updated_at?: string;
}

export interface DashboardData {
  totalIncome: number;
  totalExpenses: number;
  monthlySavings: number;
  totalNetWorth: number;
  expensesByCategory: Array<{
    name: string;
    color: string;
    total: number;
  }>;
}

export interface ProjectionData {
  current_net_worth: number;
  monthly_savings: number;
  projections: Array<{
    period?: string;
    months: number;
    projected_value: number;
    savings_added: number;
  }>;
}

class FinancialAPI {
  // Authentication
  async login(username: string, password: string): Promise<any> {
    const response = await api.post('/api.php/login', { username, password });
    return response.data;
  }

  async logout(): Promise<any> {
    const response = await api.post('/api.php/logout');
    return response.data;
  }

  async checkAuth(): Promise<any> {
    const response = await api.get('/api.php/auth-check');
    return response.data;
  }

  // Expenses
  async getExpenses(): Promise<Expense[]> {
    const response = await api.get('/api.php/expenses');
    return response.data.map((expense: any) => ({
      ...expense,
      amount: Number(expense.amount) || 0,
      category_id: Number(expense.category_id) || 0,
      id: expense.id ? Number(expense.id) : undefined,
    }));
  }

  async createExpense(expense: Omit<Expense, 'id'>): Promise<any> {
    const response = await api.post('/api.php/expenses', expense);
    return response.data;
  }

  async updateExpense(expense: Expense): Promise<any> {
    const response = await api.put('/api.php/expenses', expense);
    return response.data;
  }

  async deleteExpense(id: number): Promise<any> {
    const response = await api.delete('/api.php/expenses', { data: { id } });
    return response.data;
  }

  // Income
  async getIncome(): Promise<Income[]> {
    const response = await api.get('/api.php/income');
    return response.data.map((income: any) => ({
      ...income,
      amount: Number(income.amount) || 0,
      id: income.id ? Number(income.id) : undefined,
    }));
  }

  async createIncome(income: Omit<Income, 'id'>): Promise<any> {
    const response = await api.post('/api.php/income', income);
    return response.data;
  }

  async updateIncome(income: Income): Promise<any> {
    const response = await api.put('/api.php/income', income);
    return response.data;
  }

  async deleteIncome(id: number): Promise<any> {
    const response = await api.delete('/api.php/income', { data: { id } });
    return response.data;
  }

  // Accounts
  async getAccounts(): Promise<Account[]> {
    const response = await api.get('/api.php/accounts');
    return response.data.map((account: any) => ({
      ...account,
      current_balance: Number(account.current_balance) || 0,
      id: account.id ? Number(account.id) : undefined,
    }));
  }

  async createAccount(account: Omit<Account, 'id'>): Promise<any> {
    const response = await api.post('/api.php/accounts', account);
    return response.data;
  }

  async updateAccount(account: Account): Promise<any> {
    const response = await api.put('/api.php/accounts', account);
    return response.data;
  }

  async deleteAccount(id: number): Promise<any> {
    const response = await api.delete('/api.php/accounts', { data: { id } });
    return response.data;
  }

  // Dashboard
  async getDashboardData(): Promise<DashboardData> {
    const response = await api.get('/api.php/dashboard');
    const data = response.data;
    
    // Ensure all numeric values are properly converted to numbers
    return {
      totalIncome: Number(data.totalIncome) || 0,
      totalExpenses: Number(data.totalExpenses) || 0,
      monthlySavings: Number(data.monthlySavings) || 0,
      totalNetWorth: Number(data.totalNetWorth) || 0,
      expensesByCategory: data.expensesByCategory?.map((category: any) => ({
        name: category.name,
        color: category.color,
        total: Number(category.total) || 0,
      })) || [],
    };
  }

  // Projections
  async getProjections(): Promise<ProjectionData> {
    const response = await api.get('/api.php/projections');
    const data = response.data;
    
    // Ensure all numeric values are properly converted to numbers
    return {
      current_net_worth: Number(data.current_net_worth) || 0,
      monthly_savings: Number(data.monthly_savings) || 0,
      projections: data.projections?.map((projection: any) => ({
        months: Number(projection.months) || 0,
        projected_value: Number(projection.projected_value) || 0,
        savings_added: Number(projection.savings_added) || 0,
      })) || [],
    };
  }
}

export const financialAPI = new FinancialAPI();
